import { useState, useCallback, useEffect } from 'react';
import { calculationsApi } from '../../../../services/api/calculations';
import { ProfitAndLoss, CashflowEntry } from '../../../../types';

/**
 * Frontend-Format für GuV-Daten (camelCase)
 */
export interface GuVYear {
  jahr: number;
  jahresenergieerloes: number;
  zinserloes: number;
  gesamterloes: number;
  laufenderAufwand: number;
  afa: number;
  zinsen: number;
  gesamtaufwand: number;
  ergebnisVorSteuern: number;
  hinzurechnung: number;
  massgebenderGewinnertrag: number;
  verlustvortrag: number;
  gewerbeFreibetragVerwendet: number;
  zuVersteuernderErtrag: number;
  gewerbesteuer: number;
  ergebnisNachSteuern: number;
}

/**
 * Frontend-Format für Cashflow-Daten (camelCase, CFADS-Methode)
 */
export interface CashflowYear {
  jahr: number;
  energieerloes: number;
  zinserloes: number;
  gesamterloes: number;
  laufenderAufwand: number;
  ergebnisVorSteuern: number;
  abschreibungen: number;
  zinsen: number;
  cfads: number;
  dscr: number;
  tilgung: number;
  cashflowNachSchuldendienst: number;
  liquiditaetsbestand: number;
}

/**
 * KPI-Kennzahlen aus der Cashflow-Berechnung
 */
export interface CashflowKPIs {
  minDscr: number | null;
  avgDscr: number | null;
  irr: number | null;
}

const mapToGuVYear = (entry: ProfitAndLoss): GuVYear => ({
  jahr: entry.jahr,
  jahresenergieerloes: entry.jahresenergieerloes,
  zinserloes: entry.zinserloes,
  gesamterloes: entry.gesamterloes,
  laufenderAufwand: entry.laufender_aufwand,
  afa: entry.afa,
  zinsen: entry.zinsen,
  gesamtaufwand: entry.gesamtaufwand,
  ergebnisVorSteuern: entry.ergebnis_vor_steuern,
  hinzurechnung: entry.hinzurechnung ?? 0,
  massgebenderGewinnertrag: entry.massgebender_gewinnertrag ?? 0,
  verlustvortrag: entry.verlustvortrag ?? 0,
  gewerbeFreibetragVerwendet: entry.gewerbe_freibetrag_verwendet ?? 0,
  zuVersteuernderErtrag: entry.zu_versteuernder_ertrag ?? 0,
  gewerbesteuer: entry.gewerbesteuer,
  ergebnisNachSteuern: entry.ergebnis_nach_steuern,
});

const mapToCashflowYear = (entry: CashflowEntry): CashflowYear => ({
  jahr: entry.jahr,
  energieerloes: entry.energieerloes,
  zinserloes: entry.zinserloes,
  gesamterloes: entry.gesamterloes,
  laufenderAufwand: entry.laufender_aufwand,
  ergebnisVorSteuern: entry.ergebnis_vor_steuern,
  abschreibungen: entry.abschreibungen,
  zinsen: entry.zinsen,
  cfads: entry.cfads,
  dscr: entry.dscr,
  tilgung: entry.tilgung,
  cashflowNachSchuldendienst: entry.cashflow_nach_schuldendienst,
  liquiditaetsbestand: entry.liquiditaetsbestand,
});

/**
 * Wendet ein CalculationPageResponse auf die State-Setter an.
 */
const applyResult = (
  result: { guv_entries: ProfitAndLoss[]; cashflow: { entries: CashflowEntry[]; min_dscr?: number; avg_dscr?: number; irr?: number } },
  setGuvData: (d: GuVYear[]) => void,
  setCashflowData: (d: CashflowYear[]) => void,
  setKpis: (k: CashflowKPIs) => void,
) => {
  setGuvData(result.guv_entries.map(mapToGuVYear));
  setCashflowData(result.cashflow.entries.map(mapToCashflowYear));
  setKpis({
    minDscr: result.cashflow.min_dscr ?? null,
    avgDscr: result.cashflow.avg_dscr ?? null,
    irr: result.cashflow.irr ?? null,
  });
};

interface UseCalculationPageDataResult {
  guvData: GuVYear[];
  cashflowData: CashflowYear[];
  kpis: CashflowKPIs;
  /** Loading-State für das initiale Laden */
  isLoading: boolean;
  /** Loading-State für Neuberechnungen (Slider-Änderung) */
  isCalculating: boolean;
  error: string | null;
  /** Explizite Neuberechnung (POST calculate-all) – nach Parameteränderungen */
  calculateAll: () => Promise<void>;
}

/**
 * Kombinierter Hook für die Kalkulationsseite.
 * 
 * Beim Mount: GET /calculation-page (liest gespeicherte Ergebnisse, kein Neurechnen).
 * Bei Parameteränderung: POST /calculate-all (berechnet neu und persistiert).
 */
export const useCalculationPageData = (projectId: string): UseCalculationPageDataResult => {
  const [guvData, setGuvData] = useState<GuVYear[]>([]);
  const [cashflowData, setCashflowData] = useState<CashflowYear[]>([]);
  const [kpis, setKpis] = useState<CashflowKPIs>({ minDscr: null, avgDscr: null, irr: null });
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mount: Gespeicherte Ergebnisse laden (GET – kein Neurechnen)
  useEffect(() => {
    if (!projectId) return;

    let cancelled = false;
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await calculationsApi.getCalculationPage(projectId);
        if (!cancelled) {
          applyResult(result, setGuvData, setCashflowData, setKpis);
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Fehler beim Laden der Kalkulationsdaten';
          setError(msg);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadData();
    return () => { cancelled = true; };
  }, [projectId]);

  // Explizite Neuberechnung (POST) – nach Slider-/Parameter-Änderung
  const calculateAll = useCallback(async () => {
    if (!projectId) return;
    setIsCalculating(true);
    setError(null);
    try {
      const result = await calculationsApi.calculateAll(projectId);
      applyResult(result, setGuvData, setCashflowData, setKpis);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Fehler bei der Berechnung';
      setError(msg);
    } finally {
      setIsCalculating(false);
    }
  }, [projectId]);

  return { guvData, cashflowData, kpis, isLoading, isCalculating, error, calculateAll };
};
